import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { FlexLayoutModule } from '@angular/flex-layout';
import { switchMap, finalize } from 'rxjs/operators';
import { saveAs } from 'file-saver';

import { ReportsService } from '@app/core/services/reports.service';
import { ReportDetails, ReportDetailsData, Report } from '@app/shared/types';
import { ReportDetailsComponent } from '../reports-details-modal/report-details.component';

@Component({
  selector: 'app-report-details-view',
  standalone: true,
  imports: [
    CommonModule,
    ReportDetailsComponent,
    MatProgressSpinnerModule,
    MatIconModule,
    MatButtonModule,
    MatTooltipModule,
    FlexLayoutModule,
    MatMenuModule
  ],
  templateUrl: './report-details-view.component.html',
  styleUrls: ['./report-details-view.component.css']
})
export class ReportDetailsViewComponent implements OnInit {
  @ViewChild('fullscreenTarget') fullscreenTarget!: ElementRef;

  public reportDetails?: ReportDetails | any;
  public isLoading = true;
  public isPreviewLoading = false;
  public hasGeneratedAtLeastOnce = false;
  public inputData?: ReportDetailsData;
  public reportPreviewHtml: SafeHtml | null | undefined = null;
  public previewError: string | null = null;
  
  public zoomLevel = 1.0;
  public isFullScreen = false;
  private lastReportConfig?: Report;

  constructor(
    private route: ActivatedRoute,
    private reportsService: ReportsService,
    private sanitizer: DomSanitizer,
    private router: Router
  ) {}

  ngOnInit(): void {
    const encodedUri = this.route.snapshot.paramMap.get('uri');
    if (encodedUri) {
      const decodedUri = decodeURIComponent(encodedUri);
      this.loadReportInitialData(decodedUri);
    } else {
      this.isLoading = false;
    }
  }

  goBack(): void {
    this.router.navigate(['/reports']);
  }

  private loadReportInitialData(uri: string): void {
    this.isLoading = true;
    this.reportsService.getReportDetails(uri).subscribe({
      next: (data: ReportDetails) => {
        this.reportDetails = data;
        this.inputData = { report: { ...data, uri: data.uri } };
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Error loading report info:', err);
        this.isLoading = false;
      }
    });
  }

  handlePreviewUpdate(event: { html?: string | null; config: Report; loading?: boolean; error?: string | null }): void {
    if (event?.loading) {
      this.previewError = null;
      this.isPreviewLoading = true;
      return;
    }

    this.isPreviewLoading = false;
    this.hasGeneratedAtLeastOnce = true;

    if (event?.error) {
      this.previewError = event.error;
      this.reportPreviewHtml = null;
      this.lastReportConfig = event.config;
      return;
    }

    if (event && event.html) {
      const htmlContent = event.html.trim();
      const isReallyEmpty = this.isPreviewEmptyTable(htmlContent);

      if (isReallyEmpty) {
        this.reportPreviewHtml = null;
      } else {
        this.reportPreviewHtml = this.sanitizer.bypassSecurityTrustHtml(htmlContent);
      }
      this.previewError = null;
      this.lastReportConfig = event.config;
    } else {
      this.reportPreviewHtml = null;
      this.previewError = null;
    }
  }

  exportReport(format: 'pdf' | 'docx' | 'xlsx'): void {
    if (!this.lastReportConfig) return;

    this.isLoading = true;
    const downloadConfig = {
      ...this.lastReportConfig,
      outputFormat: format,
      ignorePagination: format === 'xlsx',
    };

    this.reportsService.runReport(downloadConfig).pipe(
      switchMap(res => this.reportsService.getReportExecutionStatus(res.requestId)),
      switchMap(res => this.reportsService.getReportOutput(res.requestId, res.exports[0].id)),
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: (blob: Blob) => {
      
        const fileName = `${this.reportDetails?.label || 'izvestaj'}.${format}`;
        
        saveAs(blob, fileName);
      },
      error: (err) => {
        console.error('Export error:', err);
      }
    });
  }

  printPdf(): void {
    if (!this.lastReportConfig) return;
    this.isLoading = true;
    const printConfig = { ...this.lastReportConfig, outputFormat: 'pdf' };

    this.reportsService.runReport(printConfig).pipe(
      switchMap(res => this.reportsService.getReportExecutionStatus(res.requestId)),
      switchMap(res => this.reportsService.getReportOutput(res.requestId, res.exports[0].id)),
      finalize(() => this.isLoading = false)
    ).subscribe({
      next: (blob: any) => {
        const blobUrl = URL.createObjectURL(blob);
        const printWindow = window.open(blobUrl, '_blank');
        if (printWindow) {
          printWindow.onload = () => {
            printWindow.print();
            setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
          };
        }
      },
      error: (err) => {
        console.error('Print error:', err);
        this.isLoading = false;
      }
    });
  }

  get isPreviewEmpty(): boolean {
    if (this.isLoading || this.isPreviewLoading) return false;
    return !this.reportPreviewHtml && !this.previewError;
  }

  isPreviewEmptyTable(content: string): boolean {
  return !content.includes('class="jrPage"');
}

  changeZoom(delta: number): void {
    this.zoomLevel = Math.max(0.5, Math.min(2.5, this.zoomLevel + delta));
  }

  toggleFullScreen(): void {
    const elem = this.fullscreenTarget.nativeElement;
    if (!document.fullscreenElement) {
      elem.requestFullscreen();
      this.isFullScreen = true;
    } else {
      document.exitFullscreen();
      this.isFullScreen = false;
    }
  }
}